import {
  CODE_PROJECTS,
  ENVIRONMENT,
} from '@elementary/common-ui/constants/queryKeys';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateCodeProjectRequest,
  UpdateCodeProjectRequest,
  DbtProject,
  CodeProjectAdditionalSettings,
} from 'shared/types';
import * as api from '../../services/settingsApi';
import * as codeProjectsApi from '../../services/codeProjectsApi';
import useSnackbar from '../useSnackbar';
import useSendTracking from './useSendTracking';

type ProjectFormData = {
  repository: string;
  branch: string;
  projectDir: string;
};

type MultiProjectSubmitData = {
  versionControl: string;
  sharedToken: string | null;
  orgUrl?: string | null;
  enterpriseUrl?: string | null;
  addLabels?: boolean | null;
  branchPrefix?: string | null;
  commitMessagePrefix?: string | null;
  requireTicketId?: boolean | null;
  projectsData: Map<string, ProjectFormData>;
  hasProjectChanges?: boolean;
};

type Props = {
  envId?: string;
  onUpdateSuccess?: () => void;
  fetchFullData?: boolean;
};

const useCodeRepositoryIntegration = ({
  envId,
  onUpdateSuccess,
  fetchFullData = false,
}: Props) => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const { tracking, trackingEvents, sendTrackingWithoutSensitiveData } =
    useSendTracking();

  const environmentQuery = useQuery(
    [ENVIRONMENT, envId],
    () => api.fetchEnvironment(envId!),
    {
      enabled: !!envId && fetchFullData,
    },
  );

  const codeProjectsQuery = useQuery(
    [CODE_PROJECTS, envId],
    () => codeProjectsApi.fetchCodeProjects(envId!),
    {
      enabled: !!envId,
    },
  );

  const dbtProjects: DbtProject[] = environmentQuery.data?.dbtProjects || [];
  const existingCodeProjects = codeProjectsQuery.data?.code_projects || [];

  const isRepositoryConnected = existingCodeProjects.length > 0;

  const codeProjectsMutation = useMutation(
    async (data: MultiProjectSubmitData) => {
      if (!envId) {
        throw new Error('No environment ID provided');
      }

      const additional_settings: CodeProjectAdditionalSettings = {};
      if (data.orgUrl !== undefined) {
        additional_settings.org_url = data.orgUrl || null;
      }
      if (data.enterpriseUrl !== undefined) {
        additional_settings.enterprise_url = data.enterpriseUrl || null;
      }
      if (data.addLabels !== undefined) {
        additional_settings.add_labels = data.addLabels;
      }
      if (data.branchPrefix !== undefined) {
        additional_settings.branch_prefix = data.branchPrefix || null;
      }
      if (data.commitMessagePrefix !== undefined) {
        additional_settings.commit_message_prefix =
          data.commitMessagePrefix || null;
      }
      if (data.requireTicketId !== undefined) {
        additional_settings.require_ticket_id = data.requireTicketId ?? null;
      }
      const hasAdditionalSettings = Object.keys(additional_settings).length > 0;

      const codeProjects = [];
      const processedDbtProjectIds = new Set<string>();

      for (const [
        dbtProjectId,
        projectFormData,
      ] of data.projectsData.entries()) {
        const isFilled = projectFormData.repository.trim() !== '';

        if (isFilled) {
          processedDbtProjectIds.add(dbtProjectId);
          codeProjects.push({
            repository: projectFormData.repository,
            branch: projectFormData.branch || null,
            project_dir: projectFormData.projectDir || null,
            dbt_project_id: dbtProjectId,
          });
        }
      }

      for (const existingProject of existingCodeProjects) {
        if (!processedDbtProjectIds.has(existingProject.dbt_project_id)) {
          codeProjects.push({
            repository: existingProject.repository,
            branch: existingProject.branch,
            project_dir: existingProject.project_dir,
            dbt_project_id: existingProject.dbt_project_id,
          });
        }
      }

      const isUpdate = isRepositoryConnected;

      if (isUpdate) {
        const hasTokenUpdate =
          data.sharedToken && data.sharedToken.trim() !== '';
        const hasProjectUpdate = data.hasProjectChanges;

        const updateData: UpdateCodeProjectRequest = {};

        if (hasTokenUpdate) {
          updateData.token = data.sharedToken;
        }

        if (hasAdditionalSettings) {
          updateData.additional_settings = additional_settings;
        }

        if (hasProjectUpdate) {
          updateData.code_projects = codeProjects;
        }

        return codeProjectsApi.updateCodeProject(envId, updateData);
      } else {
        const createData: CreateCodeProjectRequest = {
          code_project_type: data.versionControl,
          token: data.sharedToken,
          additional_settings: hasAdditionalSettings
            ? additional_settings
            : null,
          code_projects: codeProjects,
        };

        return codeProjectsApi.setCodeProject(envId, createData);
      }
    },
    {
      onSuccess: () => {
        onUpdateSuccess?.();
        queryClient.invalidateQueries([CODE_PROJECTS, envId]);
        queryClient.invalidateQueries([ENVIRONMENT]);
        openSnackbar({
          message: 'Successfully connected to your code repository',
          variant: 'success',
        });
        tracking?.sendTrackingEvent({
          eventName:
            trackingEvents?.CONNECT_CODE_REPOSITORY_INTEGRATION_SUCCESSFULLY,
        });
      },
      onError: (error: MutationError) =>
        sendTrackingWithoutSensitiveData({
          error,
          eventName:
            trackingEvents?.CONNECT_CODE_REPOSITORY_INTEGRATION_FAILURE,
        }),
    },
  );

  const isLoading = fetchFullData
    ? codeProjectsQuery.isLoading || environmentQuery.isLoading
    : codeProjectsQuery.isLoading;

  const requireTicketId =
    codeProjectsQuery.data?.additional_settings?.require_ticket_id === true;

  return {
    environmentQuery,
    codeProjectsQuery,
    codeProjectsMutation,
    dbtProjects,
    existingCodeProjects,
    additionalSettings: codeProjectsQuery.data?.additional_settings,
    codeProjectType: codeProjectsQuery.data?.code_project_type,
    isRepositoryConnected,
    shouldConnectCodeRepo: !isRepositoryConnected && !!envId,
    isLoading,
    requireTicketId,
  };
};

export default useCodeRepositoryIntegration;
